import { useState, useRef } from "react";
import toast from "react-hot-toast";
import styles from "../../styles/Appointment.module.css";
import 'react-phone-number-input/style.css'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import { Country, City }  from 'country-state-city';
import { Player } from '@lottiefiles/react-lottie-player';
import emailjs from '@emailjs/browser';

const Appointment = (props) => {

    const form = useRef();

    // Full Name
    let [fullName, setFullName] = useState('');
    const handleFullNameChange = (e) => setFullName(e.target.value);
    // Gender
    let [gender, setGender] = useState('Female');
    const handleGenderChange = (e) => setGender(e.target.value);
    // WhatsApp and Secondary Contact Numbers
    let [whatsAppContactNumber, setWhatsAppContactNumber] = useState();
    let [secondaryContactNumber, setSecondaryContactNumber] = useState('');
    // Email
    let [email, setEmail] = useState('');
    const handleEmailChange = (e) => setEmail(e.target.value);
    
    // Country and City
    const countriesList = Country.getAllCountries().map(c => { return { name: c.name, code: c.isoCode }; }); // full list of countries' name and code
    const citiesList = City.getAllCities().map(c => { return { name: c.name, code: c.countryCode }; }); // full list of cities' name and code
    
    let [shownCitiesList, setShownCitiesList] = useState( citiesList.filter(c => c.code==='PK') ); // cities shown list (i.e. the selected countries cities)
    
    let [country, setCountry] = useState('PK');
    let [city, setCity] = useState( citiesList.filter(c => c.code==='PK')[0].name );

    const handleCountryChange = (e) => {
        setCountry(e.target.value);

        // after setting the selected country - the cities dropdown will also be updated depending on the selected country
        let selectedCountryCode = countriesList.filter(c => c.code === e.target.value)[0].code; // getting the selected countries code
        setShownCitiesList( citiesList.filter(c => c.code===selectedCountryCode) ); // updating the shown cities list based on the selected country
        
        // also selecting the first city from the updated cities list
        if( citiesList.filter(c => c.code===selectedCountryCode)[0] ) {
            setCity( citiesList.filter(c => c.code===selectedCountryCode)[0].name );
        }
        else { setCity(''); } // if there is no city in the selected country
    };
    const handleCityChange = (e) => setCity(e.target.value);
    
    // Shipping Address
    let [shippingAddress, setShippingAddress] = useState('');
    const handleShippingAddressChange = (e) => setShippingAddress(e.target.value);

    // Reason of Appointment
    let [reasonOfAppointment, setReasonOfAppointment] = useState('');
    const handleReasonOfAppointmentChange = (e) => setReasonOfAppointment(e.target.value);

    // To disable the inputs and button when email is being sent...
    let [isSubmittingForm, setIsSubmittingForm] = useState(false);
    let [formSuccessfullySubmitted, setFormSuccessfullySubmitted] = useState(false);

    // on submitting the appointment form
    const submitAppointmentForm = (e) => {
        e.preventDefault();
        setIsSubmittingForm(true); // disabling the form

        // validating the fields
        let invalidToastString = '';

        let fullNameRegex = /^[a-zA-Z]+ [a-zA-Z]+$/;
        if( !fullNameRegex.test(fullName) ) { invalidToastString = 'Full Name'; }
        if( !isPossiblePhoneNumber(whatsAppContactNumber) ) { invalidToastString = 'WhatsApp Contact Number'; }
        if( !isPossiblePhoneNumber(secondaryContactNumber) ) { invalidToastString = 'Secondary Contact Number'; }
        
        if( invalidToastString!=='' ) { 
            toast.error(`${invalidToastString} is invalid.`, {duration: 3000, position: 'top-center', className: 'toast'});
            setIsSubmittingForm(false); // enabling the form
            return;
        }

        if( gender === 'Male' )
        {
            toast.error(`Sorry, Currently we are only accepting female appointments.`, {duration: 5000, position: 'top-center', className: 'toast text-center'});
            setIsSubmittingForm(false); // enabling the form
            return;
        }

        // emailing the details to the nhc account
        // https://www.abstractapi.com/guides/react-send-email-from-your-app-without-a-backend
        try {
            emailjs.sendForm(process.env.REACT_APP_EMAIL_SERVICE_ID, process.env.REACT_APP_EMAIL_TEMPLATE_ID, form.current, process.env.REACT_APP_EMAIL_PUBLIC_KEY).then((result) => {
                console.log("Result: ", result.text);
                if( result.status === 200 )
                {
                    toast.success(`Your Appointment has been submitted. We will contact you soon I.A.`, {duration: 5000, position: 'top-center', className: 'toast'});
                    setFormSuccessfullySubmitted(true); // showing success animation
                    setTimeout(()=>{ props.showInitialScreen(); }, 5000); // redirecting to initial screen
                }
            });
        }
        catch(error) {
            toast.error(`An Error Occurred. Please Try Again.`, {duration: 5000, position: 'top-center', className: 'toast'});
            setIsSubmittingForm(false); // enabling the form
            console.log("Error: ", error);
        }
    };

    if( !formSuccessfullySubmitted )
    {
        return (
            <div className="h-full w-full flex justify-center items-center">
    
                <button type="button" className={`${styles.backButton} rounded-md border border-gray-300 bg-black p-3 font-medium leading-4 text-white shadow-sm`} onClick={props.showInitialScreen}>Back</button>
    
                <form ref={form} className={`${styles.appointmentForm} ${isSubmittingForm?'hidden':'block'} px-4 py-4 bg-blue-100 rounded-md shadow-md`} onSubmit={submitAppointmentForm}>
    
                    <div className={`${styles.formContentDiv} w-full flex flex-col justify-center items-start`}>
    
                        <h2 className="font-black text-2xl text-center w-full mb-6 underline underline-offset-8 decoration-double decoration-slate-500">Appointment Form</h2>
                        <p className="w-full text-center mb-4">Fill the following form and click on the button at the end to make your appointment.</p>
                        
                        {/* Full Name */}
                        <label htmlFor="full_name" className="text-lg mb-1 font-semibold">Full Name:</label>
                        <input name="full_name" required disabled={isSubmittingForm} type="text" id="full_name" className="w-full rounded-md border border-gray-400 mb-3 px-3 py-2" placeholder="Enter your full name here..." value={fullName} onChange={handleFullNameChange} />
    
                        {/* Gender */}
                        <label className="text-lg mb-1 font-semibold">Gender:</label>
                        <div className="w-full flex justify-start items-center mb-3">
                            <label className="mr-4">
                                <input disabled={isSubmittingForm} type="radio" name="gender" className="mr-1" value="Male" checked={gender==='Male'} onChange={handleGenderChange} />Male
                            </label>
                            <label>
                                <input disabled={isSubmittingForm} type="radio" name="gender" className="mr-1" value="Female" checked={gender==='Female'} onChange={handleGenderChange} />Female
                            </label>
                        </div>
    
                        {/* WhatsApp Contact Number */}
                        <label htmlFor="WhatsAppContactInput" className="text-lg mb-1 font-semibold">WhatsApp Contact Number:</label>
                        <PhoneInput name="whatsApp_contact_number" required disabled={isSubmittingForm} id="WhatsAppContactInput" className="w-full rounded-md border border-gray-400 mb-3 px-3 py-2 bg-white" placeholder="Enter your WhatsApp contact no. here..." value={whatsAppContactNumber} onChange={setWhatsAppContactNumber}
                            defaultCountry="PK" />
    
                        {/* Secondary Contact Number */}
                        <label htmlFor="secondaryContactInput" className="text-lg mb-1 font-semibold">Secondary Contact Number:</label>
                        <PhoneInput name="secondary_contact_number" required disabled={isSubmittingForm} id="secondaryContactInput" className="w-full rounded-md border border-gray-400 px-3 py-2 bg-white mb-1" placeholder="Enter your secondary contact no. here..." value={secondaryContactNumber} onChange={setSecondaryContactNumber}
                            defaultCountry="PK" />
                        <span className="text-gray-700 text-sm mb-3">Note: Use the same WhatsApp Contact Number if you don't have a secondary one.</span>
    
                        {/* Email */}
                        <label htmlFor="emailInput" className="text-lg mb-1 font-semibold">Email:</label>
                        <input name="email" required disabled={isSubmittingForm} type="email" id="emailInput" className="w-full rounded-md border border-gray-400 mb-3 px-3 py-2" placeholder="Enter your email here..." value={email} onChange={handleEmailChange} />
    
                        {/* Country */}
                        <label htmlFor="countryInput" className="text-lg mb-1 font-semibold">Country:</label>
                        <select name="country" required disabled={isSubmittingForm} id="countryInput" className="w-full rounded-md border border-gray-400 mb-3 px-3 py-2" value={country} onChange={handleCountryChange}>
                            {
                                countriesList.map((country, index) => (
                                    <option key={index} value={country.code}>
                                        {country.name}
                                    </option>
                                ))
                            }
                        </select>
    
                        {/* City */}
                        <label htmlFor="cityInput" className="text-lg mb-1 font-semibold">City:</label>
                        <select name="city" required disabled={isSubmittingForm} id="cityInput" className="w-full rounded-md border border-gray-400 mb-3 px-3 py-2" value={city} onChange={handleCityChange}>
                            {
                                shownCitiesList.map((city, index) => (
                                    <option key={index} value={city.name}>
                                        {city.name}
                                    </option>
                                ))
                            }
                        </select>
    
                        {/* Shipping Address */}
                        <label htmlFor="shippingAddressInput" className="text-lg mb-1 font-semibold">Shipping Address:</label>
                        <textarea name="shipping_address" required disabled={isSubmittingForm} rows="4" id="shippingAddressInput" className={`${styles.shippingAddressTextArea} w-full rounded-md border border-gray-400 mb-5 px-3 py-2`} placeholder="Enter your shipping address here..." value={shippingAddress} onChange={handleShippingAddressChange}></textarea>

                        {/* Reason of Appointment */}
                        <label htmlFor="detailInput" className="text-lg mb-1 font-semibold">Reason of Appointment:</label>
                        <textarea name="reason_of_appointment" required disabled={isSubmittingForm} rows="4" id="detailInput" className={`${styles.reasonOfAppointmentTextArea} w-full rounded-md border border-gray-400 mb-5 px-3 py-2`} placeholder="Enter the reason of your appointment here..." value={reasonOfAppointment} onChange={handleReasonOfAppointmentChange}></textarea>
    
                        <button disabled={isSubmittingForm} type="submit" className="w-full rounded-md border border-gray-300 bg-black p-3 font-medium leading-4 text-white shadow-sm enabled:hover:bg-gray-800">Submit Appointment Form</button>
    
                    </div>
                    
                </form>
    
                <Player autoplay loop speed="1" src="/animations/loading.json" className={`${isSubmittingForm?'block':'hidden'}`}></Player>
    
            </div>
        );
    }
    else {
        return (
            <div className="h-full w-full flex justify-center items-center">
                <Player autoplay loop={false} keepLastFrame={true} speed="1" src="/animations/emailSent.json"></Player>
            </div>
        );
    }
}

export default Appointment;
